import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";

export default function About() {
  return (
    <div className="mx-auto max-w-[1400px] px-6 md:px-12 lg:px-16 py-20 md:py-28">
      <div className="text-xs tracking-[0.3em] uppercase text-zinc-500">About Sleek</div>
      <h1 className="font-display text-5xl md:text-7xl font-light tracking-tight mt-4 max-w-5xl leading-[1.0]">
        A design firm that knows<br />how to swing a hammer.
      </h1>

      <div className="grid lg:grid-cols-12 gap-12 mt-16">
        <div className="lg:col-span-7">
          <img src="/photos/exterior/b_double_porch.jpg" alt="Sleek project — colonial double porch" className="w-full h-full min-h-[60vh] object-cover" />
        </div>
        <div className="lg:col-span-5 lg:pl-6 flex flex-col justify-center">
          <p className="text-zinc-700 leading-relaxed text-lg">
            Our story began in the spring of 1999, when we built a new home in Lunenburg—and we
            haven't looked back since.
          </p>
          <p className="text-zinc-700 leading-relaxed mt-6">
            Over the years, we have completed a wide range of projects, including new construction,
            additions, kitchens, bathrooms, finished basements, attic renovations, decks, and more.
            While we continue to offer comprehensive remodeling services, we have made kitchens and
            bathrooms our primary focus.
          </p>
          <p className="text-zinc-700 leading-relaxed mt-6">
            To make the remodeling process easier and more seamless for our clients, we opened our
            own kitchen and bath showroom. This allows us to guide each project from the initial
            design and product-selection process through construction and final installation—all
            under one roof.
          </p>
          <p className="text-zinc-700 leading-relaxed mt-6">
            Our in-house designer, experienced project managers, and skilled finish carpenters are
            ready to put decades of knowledge, craftsmanship, and attention to detail to work on
            your home.
          </p>
          <p className="text-zinc-700 leading-relaxed mt-6 font-medium">
            One team. One standard. Every project.
          </p>
        </div>
      </div>

      <div className="grid md:grid-cols-3 gap-8 mt-24">
        {[
          { n: "01", t: "Designed first", d: "Every project starts in our showroom with a designer — not a salesperson — laying out cabinetry, countertops and hardware you can actually touch." },
          { n: "02", t: "Built in-house", d: "Our finish carpenters and project managers are employees, not subs. One team, one schedule, one mission." },
          { n: "03", t: "Communication is key", d: "We provide ongoing communication throughout the project, with updates as scheduling, selections, and site conditions evolve." },
        ].map((s) => (
          <div key={s.n} className="border-t border-zinc-200 pt-6">
            <div className="text-xs tracking-[0.3em] text-zinc-500">{s.n}</div>
            <div className="font-display text-2xl mt-4">{s.t}</div>
            <p className="text-zinc-600 mt-3 leading-relaxed">{s.d}</p>
          </div>
        ))}
      </div>

      {/* Recent work — humanizes the brand + drives portfolio engagement */}
      <div className="mt-24" data-testid="about-craft-grid">
        <div className="flex items-end justify-between flex-wrap gap-4 border-b border-zinc-200 pb-6">
          <div>
            <div className="text-xs tracking-[0.3em] uppercase text-zinc-500">Recent work</div>
            <div className="font-display text-3xl md:text-4xl font-light tracking-tight mt-3">
              Real rooms, real clients.
            </div>
          </div>
          <p className="text-zinc-600 max-w-md text-sm leading-relaxed">
            A quick cross-section of jobs we&apos;ve delivered across MetroWest. Every photo is a Sleek build — no stock, no staging.
          </p>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-3 md:gap-4 mt-8">
          {[
            { id: "p25", src: "/photos/marble_spa/shower.webp",        caption: "Skylit marble spa bath" },
            { id: "p1",  src: "/photos/wellesley/kitchen/12.jpg",      caption: "Wellesley estate kitchen" },
            { id: "p47", src: "/photos/watertown_kitchen/01_island_seating_wide.webp", caption: "Watertown historic-home kitchen" },
            { id: "p3",  src: "/photos/blumwayland/kitchen/8.jpg",     caption: "Wayland open-plan kitchen" },
            { id: "p18", src: "/photos/vault2/n11_vault_island_wide.webp", caption: "Concord vaulted cottage kitchen" },
            { id: "p4",  src: "/photos/ext3/e4_bluesky.jpg",           caption: "Two-story porch addition" },
          ].map((p, i) => (
            <Link
              key={p.id}
              to={`/portfolio/${p.id}`}
              className="group block"
              data-testid={`about-craft-${i}`}
            >
              <div className="aspect-square overflow-hidden bg-zinc-100">
                <img
                  src={p.src}
                  alt={p.caption}
                  loading="lazy"
                  className="w-full h-full object-cover group-hover:scale-105 transition duration-700"
                />
              </div>
              <div className="text-[10px] tracking-[0.25em] uppercase text-zinc-500 mt-3 group-hover:text-zinc-950 transition">
                {p.caption} <span className="opacity-50">→</span>
              </div>
            </Link>
          ))}
        </div>
      </div>

      <div className="mt-24 border border-zinc-200 p-10 md:p-14 flex flex-col md:flex-row gap-8 md:items-center md:justify-between">
        <div>
          <div className="font-display text-3xl md:text-4xl font-light">Come meet the team.</div>
          <p className="text-zinc-600 mt-2">Stop by the Northborough showroom — coffee&apos;s on us.</p>
        </div>
        <Link to="/booking" className="bg-zinc-950 text-white px-7 py-4 text-sm hover:bg-zinc-800 transition flex items-center gap-2 w-fit" data-testid="about-cta">
          Book a consultation <ArrowUpRight size={14} />
        </Link>
      </div>
    </div>
  );
}
